// @owner ClientPlatform

import type {FC, ReactNode} from 'react';
import React, {useState} from 'react';
import {styled} from 'styled-components';
import {textStyleMixin} from './text';
import clearIconSrc from '../../assets/x-circle.svg';
import eyeIconSrc from '../../assets/eye.svg';
import eyeOffIconSrc from '../../assets/eye-off.svg';

const StyledWrapper = styled.div`
  width: 100%;
`;

// Visually hidden, but present for accessibility.
const StyledVisuallyHiddenLabel = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const StyledField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: 6px;
  background-color: var(--color-input-default);
  outline: 2px solid transparent;
  transition: outline-color 100ms ease, background-color 100ms ease;

  &:hover {
    background-color: var(--color-input-hover);
  }

  &:focus-within {
    outline-color: var(--color-border-focus);
  }

  &[aria-invalid='true'] {
    background-color: var(--color-input-error);

    &:hover {
      background-color: var(--color-input-error-hover);
    }

    &:focus-within {
      outline-color: var(--color-border-danger);
    }
  }

  &[aria-disabled='true'] {
    background-color: var(--color-bg-surface-primary);
    outline-color: var(--color-border-disabled-secondary);
    cursor: not-allowed;

    &:hover {
      background-color: var(--color-bg-surface-primary);
    }
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  color: var(--color-text-primary);
  text-overflow: ellipsis;
  ${textStyleMixin({$variant: 'body-regular'})}

  &::placeholder {
    ${textStyleMixin({$variant: 'body-regular', $color: 'disabled'})}
  }

  &:disabled {
    color: var(--color-text-disabled);
    cursor: not-allowed;
  }
`;

const StyledSuffix = styled.span`
  flex: none;
  white-space: nowrap;
  ${textStyleMixin({$variant: 'body-regular', $color: 'disabled'})}
`;

const StyledIconButton = styled.button`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const StyledError = styled.span`
  ${textStyleMixin({$variant: 'caption-regular'})}
  display: block;
  margin-top: 4px;
  text-align: left;
  color: var(--color-text-danger);
`;

interface InputProps {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  suffix?: ReactNode;
  onBlur?: () => void;
  className?: string;
}

export const Input: FC<InputProps> = ({
  value,
  onChange,
  type = 'text',
  id,
  name,
  label,
  placeholder,
  error,
  required,
  autoFocus,
  disabled,
  clearable = true,
  suffix,
  onBlur,
  className,
}) => {
  const [revealed, setRevealed] = useState(false);

  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : type;

  // Invalid state comes only from the controlled error prop (which forms set on
  // blur/submit), never from native :invalid — so it never flashes while typing.
  const showInvalid = Boolean(error);
  const showClear = clearable && value.length > 0 && !disabled && !suffix;
  const showReveal = isPassword && value.length > 0 && !disabled;

  return (
    <StyledWrapper className={className}>
      {label ? <StyledVisuallyHiddenLabel htmlFor={id}>{label}</StyledVisuallyHiddenLabel> : null}
      <StyledField aria-invalid={showInvalid} aria-disabled={Boolean(disabled)}>
        <StyledInput
          id={id}
          name={name}
          type={effectiveType}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={showInvalid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
        {suffix ? <StyledSuffix>{suffix}</StyledSuffix> : null}
        {showClear ? (
          <StyledIconButton
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange('')}
          >
            <img src={clearIconSrc} alt="" />
          </StyledIconButton>
        ) : null}
        {showReveal ? (
          <StyledIconButton
            type="button"
            tabIndex={-1}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setRevealed((revealedState) => !revealedState)}
          >
            <img src={revealed ? eyeOffIconSrc : eyeIconSrc} alt="" />
          </StyledIconButton>
        ) : null}
      </StyledField>
      {showInvalid ? <StyledError>{error}</StyledError> : null}
    </StyledWrapper>
  );
};
